<template>
  <div
    class="nav-menu-item"
    :class="[
      {
        'is-open': isOpen,
        'is-closed': !isOpen,
        'is-active': isActive,
        'is-disabled': disabled,
        'is-hover': isHover,
        'bottom-group': isBottomGroup,
      },
    ]"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <router-link
      v-if="to"
      :to="to"
      class="nav-link"
      :class="{ 'disabled-link': disabled }"
    >
      <div class="icon-wrapper">
        <v-icon
          :name="icon"
          width="20"
          height="20"
          :stroke="isActive ? '#3b82f6' : 'var(--color-primary-dark)'"
          :action-color="isActive ? '#3b82f6' : 'var(--color-primary-dark)'"
          :class="{ 'icon-active': isActive }"
        />
      </div>
      <span
        v-if="isOpen && !isBottomGroup"
        class="nav-text"
        :class="{ 'text-active': isActive }"
      >
        {{ text }}
      </span>
    </router-link>

    <div
      v-else
      class="nav-link"
      :class="{ 'disabled-link': disabled }"
      @click="handleClick"
    >
      <div class="icon-wrapper">
        <v-icon
          :name="icon"
          width="20"
          height="20"
          :class="{ 'icon-active': isActive }"
        />
      </div>
      <span
        v-if="isOpen && !isBottomGroup"
        class="nav-text"
        :class="{ 'text-active': isActive }"
      >
        {{ text }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

// Props
const props = defineProps({
  text: {
    type: String,
    required: true,
  },
  icon: {
    type: String,
    required: true,
  },
  to: {
    type: [String, Object],
    default: null,
  },
  isOpen: {
    type: Boolean,
    default: true,
  },
  isActive: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  group: {
    type: String,
    default: null,
  },
});

// Emits
const emit = defineEmits(['click']);

// Reactive state
const isHover = ref(false);

// Computed properties
const isClosed = computed(() => !props.isOpen);
const isBottomGroup = computed(() => props.group === 'bottom');

// Methods
const handleMouseEnter = () => {
  if (!props.disabled) {
    isHover.value = true;
  }
};

const handleMouseLeave = () => {
  isHover.value = false;
};

const handleClick = () => {
  if (!props.disabled) {
    emit('click');
  }
};
</script>

<style lang="scss" scoped>
.nav-menu-item {
  display: flex;
  align-items: center;
  min-height: 42px;
  border-radius: 12px;
  transition: all 0.2s ease;
  cursor: pointer;
  background-color: transparent;

  // Open state
  &.is-open {
    padding: 0 16px;
    justify-content: flex-start;
    gap: 8px;
    width: 208px;
    height: 42px;

    .nav-link {
      width: 100%;
      justify-content: flex-start;
      flex: 1;
    }
  }

  // Closed state
  &.is-closed {
    padding: 0 16px;
    justify-content: center;
    width: 60px;

    .nav-link {
      width: 100%;
      justify-content: center;
      flex: 1;
    }
  }

  // Active state
  &.is-active {
    background-color: rgba(108, 161, 248, 0.1);

    .icon-wrapper {
      .v-icon {
        stroke: #3b82f6;
      }
    }

    .nav-text {
      color: #3b82f6;
      opacity: 0.6;
    }
  }

  // Hover state
  &.is-hover:not(.is-active):not(.is-disabled) {
    background-color: transparent;

    .nav-text {
      color: rgba(14, 14, 34, 0.8);
    }

    .icon-wrapper {
      .v-icon {
        stroke: rgba(14, 14, 34, 0.8);
      }
    }
  }

  // Disabled state
  &.is-disabled {
    opacity: 0.5;
    cursor: not-allowed;

    .nav-link {
      pointer-events: none;
    }
  }

  // Bottom group specific styles
  &.bottom-group {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    .nav-link {
      justify-content: center;
    }

    &.is-closed {
      .nav-text {
        display: none;
      }
    }
  }

  .nav-link {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    height: 100%;
    text-decoration: none;
    color: rgba(14, 14, 34, 0.6);
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
    font-size: 16px;
    line-height: 1.2;
    transition: color 0.2s ease;
    cursor: pointer;

    &.disabled-link {
      pointer-events: none;
    }
  }

  .icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    flex-shrink: 0;

    .v-icon {
      stroke: rgba(14, 14, 34, 0.6);
      transition: stroke 0.2s ease;

      &.icon-active {
        stroke: #3b82f6;
      }
    }
  }

  .nav-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: color 0.2s ease;

    &.text-active {
      color: #3b82f6;
    }
  }
}
</style>
